import React, { useEffect, useState } from "react";

export default function SystemStatsPanel({ hostname = window.location.hostname }) {
  const [info, setInfo] = useState({
    time: new Date().toLocaleTimeString(),
    publicIp: "loading...",
    localIp: "n/a",
    dns: "n/a",
    gpu: "unknown",
    ua: navigator.userAgent,
    platform: navigator.platform,
    languages: navigator.languages?.join(", ") || navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
    connection: navigator.connection
      ? `${navigator.connection.effectiveType} ${Math.round(navigator.connection.downlink || 0)} Mbps`
      : "unknown",
    battery: null,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || navigator.msDoNotTrack || "unknown",
  });

  const [glitchLine, setGlitchLine] = useState(null);

  useEffect(() => {
    let mounted = true;

    const updateTime = () => mounted && setInfo((prev) => ({ ...prev, time: new Date().toLocaleTimeString() }));
    const timer = setInterval(updateTime, 1000);

    // GPU detection
    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      if (gl) {
        const dbg = gl.getExtension("WEBGL_debug_renderer_info");
        const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
        mounted && setInfo((prev) => ({ ...prev, gpu: renderer || "unknown" }));
      }
    } catch {}

    // Public IP
    (async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const json = await res.json();
        mounted && setInfo((prev) => ({ ...prev, publicIp: json.ip || "unknown" }));
      } catch { mounted && setInfo((prev) => ({ ...prev, publicIp: "failed" })); }
    })();

    // Local IP detection
    (async () => {
      try {
        const ips = new Set();
        const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
        pc.createDataChannel("");
        pc.onicecandidate = (e) => {
          if (!e.candidate?.candidate) return;
          e.candidate.candidate.split(" ").forEach((p) => /(\d{1,3}\.){3}\d{1,3}/.test(p) && ips.add(p));
          if (ips.size && mounted) setInfo((prev) => ({ ...prev, localIp: Array.from(ips).join(", ") }));
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        setTimeout(() => pc.close(), 2000);
      } catch { mounted && setInfo((prev) => ({ ...prev, localIp: "unavailable" })); }
    })();

    // DNS lookup
    (async () => {
      try {
        const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(hostname)}&type=A`;
        const res = await fetch(url, { headers: { Accept: "application/dns-json" } });
        const json = await res.json();
        if (mounted) {
          const addrs = json.Answer?.map((a) => a.data).join(", ") || "no-answer";
          setInfo((prev) => ({ ...prev, dns: addrs }));
        }
      } catch { mounted && setInfo((prev) => ({ ...prev, dns: "failed" })); }
    })();

    // Battery info
    if (navigator.getBattery) {
      navigator.getBattery().then((bat) => {
        const updateBattery = () => mounted && setInfo((prev) => ({
          ...prev,
          battery: `${Math.round(bat.level * 100)}% ${bat.charging ? "Plugged In" : ""}`,
        }));
        updateBattery();
        bat.addEventListener("levelchange", updateBattery);
        bat.addEventListener("chargingchange", updateBattery);
      });
    }

    // Random glitch effect
    const keys = ["time","publicIp","localIp","dns","gpu","platform","ua","languages","screen","connection","battery"];
    const glitchTimer = setInterval(() => {
      setGlitchLine(keys[Math.floor(Math.random() * keys.length)]);
      setTimeout(() => setGlitchLine(null), 400 + Math.random() * 600);
    }, 5000);

    return () => { mounted = false; clearInterval(timer); clearInterval(glitchTimer); };
  }, [hostname]);

  const renderLine = (label, value, key) => (
    <div key={key} className={`stats-line ${glitchLine === key ? "text-freak" : ""}`}>
      {label}: {value}
    </div>
  );

  return (
    <div className="stats-panel">     
      
      <div className="stats-group">
        {renderLine("_Status", 'ONLINE', 'status')}
        {renderLine("_Time", info.time, "time")}
        {renderLine("_Threats", '0 detected', 'threats')}        
        {renderLine("_Public IP", info.publicIp, "publicIp")}
        {renderLine("_Local IP", info.localIp, "localIp")}
        {renderLine("_DNS (A)", info.dns, "dns")}
      </div>

      <div className="stats-group">
        {/*renderLine("_GPU", info.gpu, "gpu")*/}
        {renderLine("_PLATFORM", info.platform, "platform")}
        {/*renderLine("_UA", info.ua, "ua")*/}
        {renderLine("_LANGS", info.languages, "languages")}
      </div>

      <div className="stats-group">
        {renderLine("_SCREEN", `${info.screen} / depth ${info.colorDepth}`, "screen")}
        {renderLine("_CONNECTION", info.connection, "connection")}
        {renderLine("_BATTERY", info.battery ?? "n/a", "battery")}
      </div>

      <div className="stats-line small">
        cookies: {info.cookiesEnabled ? "Y" : "N"} • DNT: {info.doNotTrack}
      </div>
    </div>
  );
}
